import React, { useEffect, useReducer, useCallback, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import { makeStyles } from "tss-react/mui";

import { showMappingPopup, showToast, hideMappingPopup } from "../../../store/actions";
import WorkspaceClass from "../../../service/workspace";
import ManageSchemaClass from "../../../service/manageSchemaService";
import Snowflake from "../../../service/snowflake";
import S3FileBrowser from "../Browsers/S3FileBrowser";
import AzureFileBrowser from "../Browsers/AzureFileBrowser";
import SharePointBrowser from "../Browsers/SharePointBrowser";
import { ButtonUI } from "../../Common";
import { regexName, regexDelimiters } from "../../../util/regularExpress";
import ResentProgresses from "./ResentProgresses";
import UploadSection from "./UploadSection";
import CommonLayout from "../../Common/Layout";
import SSHFileBrowser from "../Browsers/SSHFileBrowser";
import { generateNewCounterName, parseFile } from "../../../util/common";
import Mapping from "./Mapping";
import { readFirstLines } from "../../../util/readParquet";
import HelpContextIcon from "../../Common/HelpPanel/HelpContextIcon";
import { checkReservedKeywords } from "../../../util/restrictedStatement";
import DataSource from "../../../service/dataSource";
import Switch from "../../Common/Switch";
import BrowseInput from "../../Common/BrowseInputForUnload";
import SmallToolbar from "../../Common/SmallToolbar";

const HEADER_HEIGHT = 64; //Height for App bar
const TITLE_HEIGHT = 60; //Height of title bar

const useStyles = makeStyles()((theme) => ({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
    paddingLeft: 30,
    paddingRight: 30,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 14,
  },
  container: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    maxHeight: `calc(100% - ${HEADER_HEIGHT + TITLE_HEIGHT + "px"})`,
    paddingLeft: "30px",
    paddingRight: "30px",
    paddingBottom: "16px",
  },
  PanelContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    "& .MuiAlert-message .MuiList-root": {
      listStyle: "square",
    },
    "& .MuiTableContainer-root": {
      borderRadius: "10px",
    },
  },
  Panel: {
    padding: "30px 40px 15px",
    borderRadius: 10,
    marginBottom: 0,
  },
  loadingAnimation: {
    animation: "spin 2s ease-in-out infinite -1.5s",
  },
}));

const initialState = {
  uploadedFile: null,
  file: "",
  tableName: "",
  tableNameError: "",
  locations: [],
  customDelimeter: "",
  fieldEnclosedBy: "",
  encoding: "",
  location: "",
  type: "",
  delimeter: "",
  path: "",
  bucket: "",
  fileType: "csv",
  hasHeaderRow: true,
  nullValue: "default",
  customNullValue: "",
  value: 0,
  destinationAction: "new",
  existingTableAction: "overwrite",
  schemaName: "",
  snowflakeSchemas: [],
  snowflakeTables: [],
  loading: false,
  openFileBrowser: false,
  container: "",
  docuList: "",
  localFileChunk: {},
  cloudFileChunk: {},
  onError: "ABORT_STATEMENT",
  skipFileError: "",
  saveAsDataset: true,
  displayName: "",
  displayNameError: "",
  currentName: "",
  nameError: "",
  unloadEnabled: false,
  unloadConnection: "",
  unloadPath: "",
};

const reducer = (state, updatedState) => {
  return { ...state, ...updatedState };
};

const suggestUniqueName = (sources, entities, displayName, tableName) => {
  // Generate unique names only if necessary
  const uniqueTableName = generateNewCounterName(
    [...(sources || []), ...(entities || [])].map((source) => source["name"]?.toLowerCase()),
    tableName,
    "_"
  );

  const uniqueDisplayName = generateNewCounterName(
    (sources || []).map((source) => source["displayName"]?.toLowerCase()),
    displayName,
    " "
  );

  return { displayName: uniqueDisplayName, currentName: uniqueTableName };
};

const validateDatasetName = (displayname, tableName) => {
  let errors = { nameError: "", displayNameError: "" };
  if (checkReservedKeywords(tableName)) errors.nameError = "Reserved keyword.";
  else if (tableName.length > 50 || tableName.length < 2)
    errors.nameError = "System name must be between 2 to 50 characters.";
  if (displayname.trim().length > 50 || displayname.trim().length < 2)
    errors.displayNameError = "Display name must be between 2 to 50 characters.";

  return errors;
};

const LocationSection = (props) => {
  const { locations, location, path, dispatchState } = props;
  return (
    <Grid container spacing={3} sx={{ alignItems: "start" }}>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
        }}
      >
        <FormControl fullWidth variant="outlined">
          <InputLabel>Connection</InputLabel>
          <Select
            value={location}
            onChange={(e) =>
              dispatchState({
                location: e.target.value,
                type: locations.find((location) => location.Stage_id === e.target.value).Detail.type,
              })
            }
            label="Connection"
          >
            {locations.map((location) => (
              <MenuItem key={location.Stage_id} value={location.Stage_id}>
                {location.Name}
                {location?.IsDataLake === 1 &&
                  (location?.Detail.type.toLowerCase() === "s3" ? (
                    <img src="/images/aws-s3-logo.png" alt="S3" style={{ width: "20px", marginLeft: "10px" }} />
                  ) : (
                    <img src="/images/azure-logo.png" alt="Azure" style={{ width: "20px", marginLeft: "10px" }} />
                  ))}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
        }}
      >
        <TextField
          fullWidth
          required
          label="Path"
          variant="outlined"
          inputProps={{ maxLength: 310, readOnly: true }}
          style={{ flex: 1 }}
          value={path}
          helperText="Supported File Type: Delimited files"
        />
      </Grid>
      <Grid
        sx={{
          mt: "12px",
        }}
        size={{
          xs: 12,
          sm: 6,
          md: 4,
        }}
      >
        <ButtonUI
          disabled={!location}
          variant="contained"
          color="secondary"
          endIcon={<UploadFileIcon />}
          style={{
            alignSelf: "center",
            marginBottom: "20px",
          }}
          onClick={() => {
            const locationDetail = locations.find((locationDetail) => locationDetail.Stage_id === location);

            if (locationDetail) {
              const Detail = locationDetail.Detail;
              dispatchState({
                bucket: Detail.url,
                openFileBrowser: true,
                ...(Detail.type.toLowerCase() === "azure" ? { container: Detail.container } : {}),
                ...(Detail.type.toLowerCase() === "sharepoint" ? { docuList: Detail.docuList } : {}),
              });
            }
          }}
        >
          Browse
        </ButtonUI>
      </Grid>
    </Grid>
  );
};

const DatasetSection = (props) => {
  const { displayName, currentName, displayNameError, nameError, dispatchState } = props;

  const handleName = useCallback(
    (str, dataState) => {
      const isValid = regexName(str, "alphaWithUnderscore");
      if (isValid) dispatchState({ [dataState]: str });
    },
    [dispatchState]
  );

  return (
    <Grid container spacing={3} sx={{ pt: 1 }}>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
        }}
      >
        <TextField
          required
          variant="outlined"
          inputProps={{ maxLength: 50 }}
          sx={{ marginRight: "10px" }}
          value={displayName}
          error={!!displayNameError}
          helperText={displayNameError}
          onChange={(e) => dispatchState({ displayName: e.target.value })}
          fullWidth
          label="Display Name"
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
        }}
      >
        <TextField
          required
          variant="outlined"
          error={!!nameError}
          inputProps={{ maxLength: 50 }}
          helperText={nameError}
          value={currentName}
          onChange={(e) => handleName(e.target.value, "currentName")}
          fullWidth
          label="System Name"
        />
      </Grid>
    </Grid>
  );
};

const RunningProgressButton = ({ Workspace_id }) => {
  const { classes } = useStyles();
  const runningProcesses = useSelector((state) => state.runningProcesses);

  return (
    runningProcesses.filter((process) => process.workspace === Workspace_id).some((process) => process.isRunning) && (
      <AutorenewIcon
        sx={{ fontSize: 20, color: "white", marginLeft: "5px" }}
        color="info"
        className={classes.loadingAnimation}
      />
    )
  );
};

const LoadData = () => {
  const [state, dispatchState] = useReducer(reducer, initialState);

  const workspace = useSelector((state) => state.workspace);
  const user = useSelector((state) => state.user);
  const config = useSelector((state) => state.config);
  const { showMapping } = useSelector((state) => state.showMapping);
  const { quickImportAllowed, fileSizeLimit, parquetFileSizeLimit, schema: defaultSchema } = workspace;
  const { Name: workspaceName, accessRole, Owner_id } = workspace.workspace;

  const {
    uploadedFile,
    file,
    tableName,
    tableNameError,
    locations,
    customDelimeter,
    fieldEnclosedBy,
    encoding,
    location,
    type,
    delimeter,
    path,
    bucket,
    fileType,
    hasHeaderRow,
    nullValue,
    customNullValue,
    value,
    destinationAction,
    existingTableAction,
    schemaName,
    snowflakeSchemas,
    snowflakeTables,
    loading,
    openFileBrowser,
    container,
    docuList,
    localFileChunk,
    cloudFileChunk,
    onError,
    saveAsDataset,
    displayName,
    displayNameError,
    currentName,
    nameError,
    unloadEnabled,
    unloadConnection,
    unloadPath,
  } = state;

  const [showResents, setShowResents] = useState(false);
  const [isSchemaLoaded, setIsSchemaLoaded] = useState(false);
  const [objectList, setObjectList] = useState([]);

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { Workspace_id } = useParams();

  const {
    fileTypes,
    delimiters,
    enclosedByOptions,
    encodingTypes,
    errorActions,
    nullValues,
    previewCount = 100,
  } = config;

  const defaultSourceValues = config.defaultSourceValues;

  const navigate = useNavigate();

  const setOpenFileBrowser = useCallback((val) => dispatchState({ openFileBrowser: val }), []);

  const cloudLocations = useMemo(
    () => locations.filter(({ Detail }) => Detail.type.toLowerCase() === "s3" || Detail.type.toLowerCase() === "azure"),
    [locations]
  );

  useEffect(() => {
    if (!quickImportAllowed) {
      dispatch(
        showToast({
          message: "Quick import module unavailable.",
          type: "error",
        })
      );
      navigate(`/my-workspaces/${Workspace_id}`);
    }
  }, [Workspace_id, dispatch, navigate, quickImportAllowed]);

  // For checking if load button should be disbaled or not
  const disableLoadButton = () => {
    const fileSectionReq = value === 0 ? file.trim() : path.trim() && location;
    const sourceReq = delimeter === "custom" ? customDelimeter : delimeter;
    const nullReq = nullValue === "custom" ? customNullValue : nullValue;
    const unloadReq =
      onError === "CONTINUE" && fileType === "csv" && !saveAsDataset && unloadEnabled
        ? unloadConnection && unloadPath
        : true;

    let datasetOrTable = currentName.trim() && displayName.trim();
    if (!saveAsDataset) {
      if (snowflakeSchemas?.length === 0) return true;
      if (destinationAction === "new") datasetOrTable = tableName.trim() && !tableNameError;
      else datasetOrTable = tableName.trim() && existingTableAction.trim();
    }

    return !(fileSectionReq && sourceReq && nullReq && datasetOrTable && unloadReq);
  };

  const handleCancel = () => {
    navigate(`/my-workspaces/${Workspace_id}`);
  };

  const generateNames = useCallback(
    async (filepath = "", isCompressionGZ = false) => {
      // Get file base name
      let filteredName = filepath.split("/").pop();
      // Remove extension
      filteredName = filteredName.slice(0, filteredName.lastIndexOf("."));
      // Remove scond extension in case compression is GZ
      if (isCompressionGZ) filteredName = filteredName.slice(0, filteredName.lastIndexOf("."));

      // Filter system name
      let systemName = filteredName
        .replace(/\s+/g, "_")
        .replace(/\W/g, "")
        .replace(/^[^a-zA-Z]+/, "");

      // Validate names
      const { displayNameError, nameError } = validateDatasetName(filteredName, systemName);

      filteredName = displayNameError ? "" : filteredName;
      systemName = nameError ? "" : systemName;
      let uniqueNames = {};

      if (!objectList.length) {
        const dataSource = new DataSource();
        const { success, data } = await dataSource.getAllObjectNames(Workspace_id);
        if (!success) return;

        if (success) {
          setObjectList(data);
          uniqueNames = suggestUniqueName(data.sources, data.entities, filteredName, systemName);
        }
      } else uniqueNames = suggestUniqueName(objectList.sources, objectList.entities, filteredName, systemName);

      return uniqueNames;
    },
    [Workspace_id, objectList]
  );

  const uploadFile = async (e) => {
    const fileData = e.target.files[0];
    e.target.value = "";

    if (fileData && fileData.name.lastIndexOf(".") > 0) {
      const fileSizeInMB = Math.round(fileData.size / 1024 / 1024);

      if (fileSizeInMB > fileSizeLimit) {
        // file size greater than defined size limit
        dispatch(
          showToast({
            message: `File size exceeded the configured value (${fileSizeLimit} MB).\nPlease move the file to S3 or Azure and retry.`,
            type: "error",
          })
        );
      } else if (fileSizeInMB > 1024 * 1024 * 1024) {
        // file size greater than 10 GB
        dispatch(
          showToast({
            message: "Please upload file having size equal or less than 10 GB.",
            type: "error",
          })
        );
      } else {
        dispatchState({ uploadedFile: fileData, file: fileData.name, loading: true });

        parseFile(
          fileData,
          async (data, func, metaData) => {
            let datasetNames = {};
            if (saveAsDataset) datasetNames = await generateNames(fileData.name, metaData?.isCompressionGZ);

            if (metaData.fileType === "parquet") {
              if (fileSizeInMB > parquetFileSizeLimit) {
                dispatchState({ uploadedFile: null, file: "", loading: false });

                // file size greater than defined size limit
                return dispatch(
                  showToast({
                    message: `File size exceeded the configured value for type parquet (${parquetFileSizeLimit} MB).\nPlease move the file to S3 or Azure and retry.`,
                    type: "error",
                  })
                );
              }

              try {
                dispatchState({
                  localFileChunk: {
                    data: await readFirstLines(fileData, previewCount),
                    fileType: fileTypes.find((f) => f.value === metaData.fileType)?.value ?? fileType,
                    hasHeaderRow: true,
                    fileName: fileData.name,
                  },
                  fileType: fileTypes.find((f) => f.value === metaData.fileType)?.value ?? fileType,
                  hasHeaderRow: true,
                  ...datasetNames,
                });
              } catch (error) {
                console.log(error);
                dispatch(
                  showToast({
                    message: "Unable to process the data. Please check your selected file or selected configurations.",
                    type: "error",
                  })
                );
              }

              dispatchState({ loading: false });
            } else {
              let metadelimiter = delimiters.find((d) => d.value === metaData.delimiter)?.value ?? delimeter;
              let findMetaData = {
                encoding:
                  encodingTypes.find((en) => en.text.toLowerCase() === metaData.encoding.toLowerCase())?.value ??
                  encoding,
                fieldEnclosedBy:
                  enclosedByOptions.find((q) => q.value === metaData.textQualifier)?.value ?? fieldEnclosedBy,
                fileType: fileTypes.find((f) => f.value === metaData.fileType)?.value ?? fileType,
                ...(metaData.nullValue
                  ? { nullValue: "custom", customNullValue: metaData.nullValue }
                  : { nullValue: "default", customNullValue: "" }),
              };
              dispatchState({
                localFileChunk: { data, ...findMetaData, delimiter: metadelimiter, fileName: fileData.name },
                ...findMetaData,
                delimeter: metadelimiter,
                ...datasetNames,
                loading: false,
              });
            }
          },
          true
        );
      }
    }
  };

  const getCloudFileData = useCallback(
    async (needFileProperties = true, newPath = "") => {
      const workspace = new WorkspaceClass();

      let bucketName = "";
      let file = "";
      let tempPath = newPath ? newPath : path;

      if (type.toLowerCase() === "s3") {
        //Setting default values based on url props
        const bucketNameArray = bucket.replace("s3://", "").split("/");
        bucketName = bucketNameArray[0];
        bucketNameArray.shift();
        file = bucketNameArray.length ? bucketNameArray.join("/") : "";
        if (bucketNameArray.length && file && !file?.endsWith("/")) file += "/";
        //Checking if path has file selected
        if (tempPath) {
          if (file?.endsWith("/")) file = file + tempPath.substring(1);
          else file = file + tempPath;
        }
      } else file = tempPath;

      const response = await workspace.loadFileColumns(Workspace_id, {
        bucket: bucketName,
        file,
        delimiter: delimeter === "custom" ? customDelimeter : delimeter,
        fieldEnclosedBy,
        encoding: encodingTypes.find((en) => en.value === encoding).text ?? "UTF-8", //provide text
        hasHeaderRow,
        location,
        append: destinationAction === "existing" && existingTableAction === "append",
        schemaName,
        tableName,
        fileType,
        previewCount,
        needFileProperties,
      });

      if (!response.success) dispatch(hideMappingPopup());

      if (response.data) response.data = JSON.parse(response.data);
      return response;
    },
    [
      path,
      type,
      Workspace_id,
      delimeter,
      customDelimeter,
      fieldEnclosedBy,
      encodingTypes,
      hasHeaderRow,
      location,
      destinationAction,
      existingTableAction,
      schemaName,
      tableName,
      fileType,
      previewCount,
      dispatch,
      bucket,
      encoding,
    ]
  );

  const handleCloudPathChange = async (newPath) => {
    dispatchState({ loading: true, path: newPath });
    const fileData = await getCloudFileData(true, newPath);
    if (fileData) {
      let data = fileData.data;

      let datasetNames = {};
      if (saveAsDataset && fileData?.success)
        datasetNames = await generateNames(data?.fileName, data?.fileProperties?.isCompressionGZ);

      const detectedDelimiter = delimiters.find((d) => d.value === data?.fileProperties?.delimiter)?.value ?? delimeter;
      let tempfileProperties = {
        encoding:
          encodingTypes.find((en) => en.text.toLowerCase() === data?.fileProperties?.encoding?.toLowerCase())?.value ??
          encoding,
        fieldEnclosedBy:
          enclosedByOptions.find((q) => q.value === data?.fileProperties?.textQualifier)?.value ?? fieldEnclosedBy,
        ...(data?.fileProperties?.nullValue
          ? { nullValue: "custom", customNullValue: data?.fileProperties?.nullValue }
          : { nullValue: "default", customNullValue: "" }),
        fileType: fileTypes.find((f) => f.value === data?.fileProperties?.fileType)?.value ?? fileType,
      };

      dispatchState({
        cloudFileChunk: {
          ...data,
          path: newPath,
          fileProperties: {
            ...tempfileProperties,
            delimiter: detectedDelimiter,
            hasHeaderRow: data?.fileProperties?.hasHeaderRow,
          },
        },
        ...tempfileProperties,
        delimeter: detectedDelimiter,
        hasHeaderRow: data?.fileProperties?.hasHeaderRow,
        ...datasetNames,
      });
    }

    dispatchState({ loading: false });
  };

  const loadSnowflakeSchemas = useCallback(async () => {
    const currentUserId = user.currentUser.User_id;

    const manageSchema = new ManageSchemaClass();
    const response = await manageSchema.getWorkspaceSchemasWithPermissions(Workspace_id);
    if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
    if (response.success) {
      const schemaArr = [];
      if (currentUserId === Owner_id) {
        response.data.schemas.length &&
          response.data.schemas.forEach((schemas) => {
            if (schemas.Name.toLowerCase() !== "model") schemaArr.push(schemas.Name);
          });
      } else {
        response.data.schemaPermissionDetails.length &&
          response.data.schemaPermissionDetails.forEach((userObj) => {
            if (currentUserId === userObj.User_id)
              Object.keys(userObj.schemas).forEach((key) => {
                if (userObj.schemas[key].Name.toLowerCase() !== "model" && userObj.schemas[key].AccessType === 2)
                  schemaArr.push(userObj.schemas[key].Name);
              });
          });
      }
      return schemaArr.length ? schemaArr.sort() : schemaArr;
    }

    return [];
  }, [Owner_id, Workspace_id, navigate, user.currentUser.User_id]);

  const loadSnowflakeTables = useCallback(
    async (schema) => {
      if (schema === "") return [];
      const snowflake = new Snowflake();
      const response = await snowflake.getTables(Workspace_id, schema.toUpperCase());
      if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
      if (response.success) return response.data;
      return [];
    },
    [Workspace_id, navigate]
  );

  const loadLocationList = useCallback(async () => {
    const workspace = new WorkspaceClass();
    const result = await workspace.getLocationList(Workspace_id);
    const filteredResult = result.filter(
      (item) => !["snowflake", "salesforce", "veevavault", "mysql"].includes(item.Detail.type.toLowerCase())
    );

    if (filteredResult?.length) return filteredResult;
    return [];
  }, [Workspace_id]);

  const loadWorkspaceDetail = useCallback(async () => {
    dispatchState({ loading: true });

    if (locations.length === 0) {
      const locations = await loadLocationList();
      dispatchState({ locations });
    }

    if (!saveAsDataset && !isSchemaLoaded) {
      const snowflakeSchemas = await loadSnowflakeSchemas();
      const schemaName = ([1, 2].includes(accessRole) ? defaultSchema : snowflakeSchemas[0]) || "";
      dispatchState({
        schemaName,
        snowflakeSchemas,
        snowflakeTables: schemaName ? await loadSnowflakeTables(schemaName) : [],
      });
      setIsSchemaLoaded(true);
    }

    dispatchState({ loading: false });
  }, [
    locations.length,
    saveAsDataset,
    isSchemaLoaded,
    loadLocationList,
    loadSnowflakeSchemas,
    accessRole,
    defaultSchema,
    loadSnowflakeTables,
  ]);

  const handleOnError = useCallback((e) => {
    dispatchState({ onError: e.target.value });
  }, []);

  const handlePreviewData = () => {
    if (saveAsDataset) {
      const { nameError, displayNameError } = validateDatasetName(displayName, currentName);
      dispatchState({ nameError, displayNameError });
      if (!nameError && !displayNameError) dispatch(showMappingPopup());
    } else dispatch(showMappingPopup());
  };

  useEffect(() => {
    if (!Workspace_id) navigate("/my-workspaces");
    else {
      dispatchState({
        fileType: defaultSourceValues.type,
        delimeter: defaultSourceValues.delimiter,
        fieldEnclosedBy: defaultSourceValues.qualifier,
        encoding: defaultSourceValues.encoding,
        onError: defaultSourceValues.errorAction,
        hasHeaderRow: true,
      });
      loadWorkspaceDetail();
    }
  }, [
    Workspace_id,
    navigate,
    loadWorkspaceDetail,
    defaultSourceValues.type,
    defaultSourceValues.delimiter,
    defaultSourceValues.qualifier,
    defaultSourceValues.encoding,
    defaultSourceValues.errorAction,
  ]);

  const handleNullValues = useCallback((e) => {
    if (e.target.value !== "custom") dispatchState({ customNullValue: "" });
    dispatchState({ nullValue: e.target.value });
  }, []);

  const handleCustomNullValue = useCallback((customNullValue) => dispatchState({ customNullValue }), []);

  const handleCustomeDelimeter = (str, key) => {
    const isValid = regexDelimiters(str);
    if (isValid) dispatchState({ [key]: str });
  };

  const handleTableName = (str, key) => {
    const isNameValid = regexName(str, "alphaWithUnderscore");
    if (isNameValid) dispatchState({ [key]: str });
  };

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
    { link: "#", displayName: "Quick Import", active: true },
  ];

  const sourceFileSection = () => {
    return (
      <Grid container spacing={3}>
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <FormControl fullWidth variant="outlined" sx={{ my: 0 }}>
            <InputLabel>File Type</InputLabel>
            <Select value={fileType} onChange={(e) => dispatchState({ fileType: e.target.value })} label="File Type">
              {fileTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {fileType.toLowerCase() !== "parquet" ? (
          <>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormControl fullWidth variant="outlined" sx={{ my: 0 }}>
                <InputLabel>Format delimiter</InputLabel>
                <Select
                  value={delimeter}
                  onChange={(e) => {
                    if (e.target.value !== "custom") dispatchState({ customDelimeter: "" });
                    dispatchState({ delimeter: e.target.value });
                  }}
                  label="Format delimiter"
                >
                  {delimiters.map((delimeter) => (
                    <MenuItem key={delimeter.value} value={delimeter.value}>
                      {delimeter.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {delimeter === "custom" ? (
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 4,
                }}
              >
                <TextField
                  fullWidth
                  required
                  label="Custom"
                  value={customDelimeter}
                  onChange={(e) => {
                    handleCustomeDelimeter(e.target.value, "customDelimeter");
                  }}
                  inputProps={{ maxLength: 2 }}
                />
              </Grid>
            ) : (
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 4,
                }}
              ></Grid>
            )}
            <Grid
              size={{
                xs: 12,
                sm: 4,
                md: 4,
              }}
            >
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Text Qualifier</InputLabel>
                <Select
                  value={fieldEnclosedBy}
                  label="Text Qualifier"
                  onChange={(e) => dispatchState({ fieldEnclosedBy: e.target.value })}
                >
                  {enclosedByOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
                md: 4,
              }}
            >
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Encoding</InputLabel>
                <Select value={encoding} label="Encoding" onChange={(e) => dispatchState({ encoding: e.target.value })}>
                  {encodingTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
                md: 4,
              }}
            >
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox checked={hasHeaderRow} onChange={(e) => dispatchState({ hasHeaderRow: !hasHeaderRow })} />
                  }
                  label="Has a header row"
                />
              </FormGroup>
            </Grid>
          </>
        ) : (
          <></>
        )}
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        >
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Null Values</InputLabel>
            <Select value={nullValue} label="Null Values" onChange={handleNullValues}>
              {nullValues?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {nullValue === "custom" && (
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <TextField
              required
              label="Custom Null Values"
              value={customNullValue}
              onChange={(e) => handleCustomNullValue(e.target.value)}
              sx={{ width: "100%" }}
              helperText="Please enter the exact comma-separated value"
            />
          </Grid>
        )}
        <Grid
          size={{
            sm: 4,
            xs: 12,
          }}
        >
          <FormControl variant="outlined" fullWidth>
            <InputLabel>On Error</InputLabel>
            <Select value={onError} label="On Error" onChange={handleOnError}>
              {$errorActions.map(({ value, text }) => (
                <MenuItem key={value} value={value}>
                  {text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  const destinationFileSection = () => {
    return (
      <RadioGroup
        value={destinationAction}
        onChange={(e) => {
          dispatchState({ destinationAction: e.target.value, tableName: "" });
        }}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <Grid container spacing={3}>
              <Grid
                alignSelf={"center"}
                size={{
                  xs: 12,
                  sm: 4,
                  md: 2,
                }}
              >
                Schema
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 4,
                }}
              >
                <FormControl fullWidth variant="outlined">
                  <Select
                    value={schemaName}
                    onChange={async (e) => {
                      dispatchState({ loading: true });
                      dispatchState({
                        schemaName: e.target.value,
                        tableName: "",
                        snowflakeTables: await loadSnowflakeTables(e.target.value),
                        loading: false,
                      });
                    }}
                  >
                    {snowflakeSchemas.map((schema) => (
                      <MenuItem key={schema} value={schema}>
                        {schema.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 2,
                }}
              >
                <FormControlLabel value="new" control={<Radio />} label="Create New" />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 4,
                }}
              >
                <TextField
                  required
                  fullWidth
                  label="Table Name"
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                  disabled={destinationAction !== "new"}
                  value={destinationAction === "new" ? tableName : ""}
                  onChange={(e) => {
                    if (checkReservedKeywords(e.target.value)) dispatchState({ tableNameError: "Reserved keyword." });
                    else if (snowflakeTables.some((table) => table.toLowerCase() === e.target.value.toLowerCase()))
                      dispatchState({ tableNameError: "Table already exists" });
                    else dispatchState({ tableNameError: "" });
                    handleTableName(e.target.value, "tableName");
                  }}
                  style={{ marginRight: "40px", flex: "1" }}
                  error={destinationAction === "new" && !!tableName && !!tableNameError}
                  helperText={destinationAction === "new" && !!tableName && tableNameError}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container spacing={3} sx={{ display: "flex", alignItems: "center" }}>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 2,
                }}
              >
                <FormControlLabel value="existing" control={<Radio />} label="Use Existing" />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4,
                  md: 4,
                }}
              >
                <FormControl
                  required
                  fullWidth
                  variant="outlined"
                  sx={{ my: 0 }}
                  disabled={destinationAction !== "existing"}
                >
                  <InputLabel>Table</InputLabel>
                  <Select
                    label="Table"
                    value={destinationAction === "existing" ? tableName : ""}
                    onChange={(e) => dispatchState({ tableName: e.target.value })}
                  >
                    {destinationAction === "existing" &&
                      snowflakeTables.map((table) => (
                        <MenuItem key={table} value={table}>
                          {table}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 4,
                  md: 6,
                }}
              >
                <RadioGroup
                  row
                  value={existingTableAction}
                  onChange={(e) => dispatchState({ existingTableAction: e.target.value })}
                >
                  <FormControlLabel
                    disabled={destinationAction !== "existing"}
                    value="overwrite"
                    control={<Radio />}
                    label="Overwrite data"
                  />
                  <FormControlLabel
                    disabled={destinationAction !== "existing"}
                    value="append"
                    control={<Radio />}
                    label="Append data"
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </RadioGroup>
    );
  };

  const mappingData = {
    value,
    schemaName,
    tableName,
    delimeter: delimeter === "custom" ? customDelimeter : delimeter,
    onError,
    nullValue: nullValue === "custom" ? customNullValue : nullValue,
    fieldEnclosedBy,
    encoding,
    fileType,
    hasHeaderRow,
    destinationAction,
    existingTableAction,
    type,
    localFileChunk,
    cloudFileChunk,
    saveAsDataset,
    displayName,
    currentName,
    unloadEnabled,
    unloadConnection,
    unloadPath,
    getCloudFileData,
    setShowResents,
    clearState: async () => {
      dispatchState({
        ...initialState,
        locations,
        delimeter: defaultSourceValues.delimiter,
        nullValue: defaultSourceValues.nullValue,
        fieldEnclosedBy: defaultSourceValues.qualifier,
        encoding: defaultSourceValues.encoding,
        onError: defaultSourceValues.errorAction,
        loading: false,
        schemaName,
        snowflakeSchemas,
        snowflakeTables,
        localFileChunk: {},
        cloudFileChunk: {},
        saveAsDataset: true,
        displayName: "",
        currentName: "",
      });
    },
    ...(value === 0
      ? { uploadedFile }
      : {
          location,
          path,
          bucket,
          locationName: location && locations?.find((loc) => loc.Stage_id === location).Name,
        }),
  };

  const $errorActions = useMemo(
    () => errorActions.filter(({ value }) => ["CONTINUE", "ABORT_STATEMENT"].includes(value)),
    [errorActions]
  );

  return (
    <CommonLayout title="Quick Import" loading={loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <AppBar
          sx={{
            position: "relative",
            boxShadow: 0,
            backgroundColor: "#e8edf1",
          }}
        >
          <Toolbar className={classes.Header}>
            <Grid container spacing={2} direction="row" alignItems={"center"}>
              <Grid
                size={{
                  sm: 12,
                  xs: 12,
                }}
              >
                <Box sx={{ display: "inline-flex", verticalAlign: "sub", marginRight: "20px" }}>
                  <Typography className={classes.HeaderTitle} gutterBottom component="div">
                    Load Data
                  </Typography>
                </Box>
                <ButtonUI
                  variant="contained"
                  color="error"
                  style={{ width: "50px", marginRight: "20px" }}
                  onClick={handleCancel}
                >
                  Cancel
                </ButtonUI>
                <ButtonUI variant="contained" color="info" onClick={() => setShowResents((prev) => !prev)}>
                  Recent Imports
                  <RunningProgressButton Workspace_id={Workspace_id} />
                </ButtonUI>
                <Box sx={{ display: "inline-flex", verticalAlign: "sub", marginRight: "20px" }}>
                  <HelpContextIcon screen="Quick Import" />
                </Box>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>

        <Box className={classes.container}>
          <Box maxWidth="lg">
            <Box>
              <SmallToolbar>{value === 0 ? "Local File" : "Cloud Storage"}</SmallToolbar>
              <Paper elevation={1} className={classes.Panel}>
                <Box sx={{ display: "flex", alignItems: "center", padding: 0 }}>
                  <RadioGroup
                    value={value}
                    onChange={(e) => dispatchState({ value: Number(e.target.value) })}
                    row
                    sx={{ margin: "0 40px 10px 0", maxWidth: "fit-content" }}
                  >
                    <FormControlLabel value={0} control={<Radio />} label="Local File" />
                    <FormControlLabel value={1} control={<Radio />} label="Cloud Storage" />
                  </RadioGroup>
                  <FormGroup sx={{ margin: "0 40px 10px 0", maxWidth: "fit-content" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={saveAsDataset}
                          onChange={(e) =>
                            dispatchState({
                              saveAsDataset: !saveAsDataset,
                              displayName: "",
                              currentName: "",
                              displayNameError: "",
                              nameError: "",
                            })
                          }
                        />
                      }
                      label="Save as Dataset"
                    />
                  </FormGroup>
                </Box>

                {value === 0 ? (
                  <UploadSection {...{ file, uploadFile }} />
                ) : (
                  <LocationSection
                    locations={locations}
                    location={location}
                    path={path}
                    dispatchState={dispatchState}
                  />
                )}

                {saveAsDataset && (
                  <DatasetSection
                    displayName={displayName}
                    currentName={currentName}
                    displayNameError={displayNameError}
                    nameError={nameError}
                    dispatchState={dispatchState}
                  />
                )}
              </Paper>
            </Box>
            <Box>
              <SmallToolbar>Source File Properties</SmallToolbar>
              <Paper elevation={1} className={classes.Panel}>
                {sourceFileSection()}
              </Paper>
            </Box>
            {!saveAsDataset && (
              <>
                {onError === "CONTINUE" && fileType === "csv" && (
                  <Box>
                    <SmallToolbar>Error Report</SmallToolbar>
                    <Paper elevation={1} className={classes.Panel}>
                      <Grid container spacing={2} sx={{ p: 0 }}>
                        <Grid sx={{ pt: 0 }} size={12}>
                          <FormControl className="switch" component="fieldset">
                            <FormGroup aria-label="position" row>
                              <FormControlLabel
                                value={unloadEnabled}
                                control={<Switch />}
                                label="Enable"
                                labelPlacement="start"
                                sx={{ margin: "0" }}
                                onChange={() => {
                                  dispatchState({
                                    unloadEnabled: !unloadEnabled,
                                    ...(unloadEnabled ? { unloadConnection: "", unloadPath: "" } : {}),
                                  });
                                }}
                                checked={unloadEnabled}
                              />
                            </FormGroup>
                          </FormControl>
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            sm: 4,
                            md: 4,
                          }}
                        >
                          <FormControl fullWidth variant="outlined" required={unloadEnabled} disabled={!unloadEnabled}>
                            <InputLabel>Connection</InputLabel>
                            <Select
                              required
                              value={unloadConnection}
                              onChange={(e) =>
                                dispatchState({
                                  unloadConnection: e.target.value,
                                })
                              }
                              label="Connection"
                              MenuProps={{ sx: { height: "220px" } }}
                            >
                              {cloudLocations.map((option) => (
                                <MenuItem key={option.Stage_id} value={option.Stage_id}>
                                  {option.Name}
                                  {option?.IsDataLake === 1 &&
                                    (option?.Detail.type.toLowerCase() === "s3" ? (
                                      <img
                                        src="/images/aws-s3-logo.png"
                                        alt="S3"
                                        style={{ width: "20px", marginLeft: "10px" }}
                                      />
                                    ) : (
                                      <img
                                        src="/images/azure-logo.png"
                                        alt="Azure"
                                        style={{ width: "20px", marginLeft: "10px" }}
                                      />
                                    ))}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid
                          size={{
                            xs: 12,
                            sm: 8,
                            md: 8,
                          }}
                        >
                          <BrowseInput
                            disabled={!unloadEnabled}
                            connections={cloudLocations}
                            connection={unloadConnection}
                            path={unloadPath}
                            setPath={(path) =>
                              dispatchState({
                                unloadPath: path,
                              })
                            }
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>
                )}

                <Box>
                  <SmallToolbar>Destination Table Properties</SmallToolbar>
                  <Paper elevation={1} className={classes.Panel}>
                    {destinationFileSection()}
                  </Paper>
                </Box>
              </>
            )}
            <Box
              sx={{
                marginTop: "10px",
              }}
            >
              <ButtonUI
                variant="contained"
                color="success"
                disabled={loading || disableLoadButton()}
                onClick={() => handlePreviewData()}
              >
                Preview Data
              </ButtonUI>
            </Box>
          </Box>
        </Box>
      </Box>
      {openFileBrowser && type.toLowerCase() === "s3" && (
        <S3FileBrowser
          setOpenFileBrowser={setOpenFileBrowser}
          url={bucket}
          path={path}
          setPath={(newPath) => handleCloudPathChange(newPath)}
          stage={location}
          locationName={location && locations?.find((loc) => loc.Stage_id === location).Name}
          fileSelection
        />
      )}
      {openFileBrowser && type.toLowerCase() === "azure" && (
        <AzureFileBrowser
          setOpenFileBrowser={setOpenFileBrowser}
          path={`/${container}${path || "/"}`}
          container={container}
          setPath={(newPath) =>
            handleCloudPathChange(
              newPath.replace(new RegExp(`^/${container}`), "").replace(new RegExp(`,/${container}`, "g"), ",")
            )
          }
          stage={location}
          locationName={location && locations?.find((loc) => loc.Stage_id === location).Name}
          fileSelection
        />
      )}
      {openFileBrowser && type.toLowerCase() === "sharepoint" && (
        <SharePointBrowser
          url={`${bucket[bucket.length - 1] === "/" ? bucket.substring(0, bucket.length - 1) : bucket}/${docuList}`}
          setOpenFileBrowser={setOpenFileBrowser}
          path={`${path || "/"}`}
          setPath={(newPath) => handleCloudPathChange(newPath)}
          stage={location}
          locationName={location && locations?.find((loc) => loc.Stage_id === location).Name}
          fileSelection
        />
      )}
      {openFileBrowser && type.toLowerCase() === "sftp" && (
        <SSHFileBrowser
          setOpenFileBrowser={setOpenFileBrowser}
          pathStr={path || "/"}
          setPath={(newPath) =>
            handleCloudPathChange(
              newPath.replace(new RegExp(`^/${container}`), "").replace(new RegExp(`,/${container}`, "g"), ",")
            )
          }
          stage={location}
          locationName={location && locations?.find((loc) => loc.Stage_id === location).Name}
          fileSelection
        />
      )}
      {showMapping && <Mapping {...mappingData} />}
      {showResents && <ResentProgresses onClose={() => setShowResents(false)} />}
    </CommonLayout>
  );
};

export default LoadData;
